import React, { useEffect, useRef, useState } from "react";
import { MoreVertical } from "lucide-react";

const baseItemClassName =
  "w-full flex items-center gap-2 px-3 py-2 text-sm text-left transition-colors hover:bg-gray-50 disabled:opacity-60 disabled:cursor-not-allowed";

export default function EllipsisMenu({
  items = [],
  align = "right",
  triggerClassName = "",
  menuClassName = "",
  buttonAriaLabel = "More options",
  disabled = false,
}) {
  const [open, setOpen] = useState(false);
  const containerRef = useRef(null);

  useEffect(() => {
    const handleOutsideClick = (event) => {
      if (containerRef.current && !containerRef.current.contains(event.target)) {
        setOpen(false);
      }
    };

    const handleEscape = (event) => {
      if (event.key === "Escape") {
        setOpen(false);
      }
    };

    document.addEventListener("mousedown", handleOutsideClick);
    document.addEventListener("keydown", handleEscape);
    return () => {
      document.removeEventListener("mousedown", handleOutsideClick);
      document.removeEventListener("keydown", handleEscape);
    };
  }, []);

  const closeMenu = () => setOpen(false);

  const renderItemIcon = (icon) => {
    if (!icon) return null;
    if (React.isValidElement(icon)) {
      return icon;
    }
    if (typeof icon === "function" || typeof icon === "object") {
      const Icon = icon;
      return <Icon className="h-4 w-4" />;
    }
    return null;
  };

  return (
    <div ref={containerRef} className="relative inline-flex">
      <button
        type="button"
        className={`h-8 w-8 rounded-lg inline-flex items-center justify-center text-gray-600 hover:bg-gray-100 transition-colors ${triggerClassName}`}
        onClick={() => setOpen((prev) => !prev)}
        aria-label={buttonAriaLabel}
        disabled={disabled}
      >
        <MoreVertical className="h-5 w-5" />
      </button>

      {open && (
        <div
          className={`absolute top-10 z-50 min-w-[160px] overflow-hidden rounded-xl border border-gray-200 bg-white shadow-lg ${
            align === "left" ? "left-0" : "right-0"
          } ${menuClassName}`}
        >
          {items.map((item, index) => {
            if (!item || item.hidden) return null;

            if (item.type === "separator") {
              return <div key={`separator-${index}`} className="h-px bg-gray-200 my-1" />;
            }

            if (typeof item.render === "function") {
              return (
                <div key={item.key || `item-${index}`}>
                  {item.render({ closeMenu })}
                </div>
              );
            }

            return (
              <button
                key={item.key || `item-${index}`}
                type="button"
                className={`${baseItemClassName} ${item.danger ? "text-red-600 hover:bg-red-50" : "text-gray-800"}`}
                disabled={item.disabled}
                onClick={async (event) => {
                  event.stopPropagation();
                  if (item.disabled) return;
                  if (typeof item.onClick === "function") {
                    await item.onClick(event);
                  }
                  if (item.closeOnClick !== false) {
                    closeMenu();
                  }
                }}
              >
                <span className="shrink-0">{renderItemIcon(item.icon)}</span>
                <span>{item.label}</span>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
